/* AMMOBOKSY storefront layer
   · window.AB.money / toast / cart helpers / section fetch (shared by all sections)
   · scroll reveal with stagger
   · dialog backdrop close + swipe-to-dismiss for bottom sheets
   Globals (moneyFormat, routes, template) are set inline in layout/theme.liquid. */
(() => {
  const AB = (window.AB = window.AB || {});

  /* ---- Money formatting (mirrors Shopify's money filter) ---------------- */
  const formatMoney = (cents, format) => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || AB.moneyFormat || '{{amount}} zł';
    const match = /\{\{\s*(\w+)\s*\}\}/.exec(fmt);
    const key = match ? match[1] : 'amount';
    const number = (n, precision, thousands, decimal) => {
      if (isNaN(n) || n == null) return '0';
      n = (n / 100).toFixed(precision);
      const parts = n.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const c = parts[1] ? decimal + parts[1] : '';
      return dollars + c;
    };
    let value = '';
    switch (key) {
      case 'amount_no_decimals':
        value = number(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = number(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = number(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = number(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = number(cents, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = number(cents, 2, "'", '.');
        break;
      case 'amount_with_period_and_space_separator':
        value = number(cents, 2, ' ', '.');
        break;
      default:
        value = number(cents, 2, ',', '.');
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value).replace(/<[^>]+>/g, '');
  };
  /* Storefront shows whole amounts without decimals ("58 zł"), like money_without_trailing_zeros */
  AB.money = (cents) => formatMoney(cents).replace(/([.,])00(?!\d)/, '');

  /* ---- Toast ------------------------------------------------------------- */
  let toastTimer;
  AB.toast = (text, link) => {
    const toast = document.getElementById('ab-toast');
    if (!toast) return;
    toast.innerHTML = '';
    toast.appendChild(document.createTextNode(text));
    if (link) {
      const a = document.createElement('a');
      a.href = link.href;
      a.textContent = link.label;
      toast.appendChild(a);
    }
    toast.hidden = false;
    requestAnimationFrame(() => toast.classList.add('ab-toast--visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toast.classList.remove('ab-toast--visible');
      toastTimer = setTimeout(() => {
        toast.hidden = true;
      }, 220);
    }, 4000);
  };

  /* ---- Cart API ---------------------------------------------------------- */
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));

  const getCart = async () => {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  };

  AB.cart = {
    get: getCart,
    /* add({id, quantity, properties}) or add(FormData) */
    add: async (payload, opts = {}) => {
      const init =
        payload instanceof FormData
          ? { method: 'POST', body: payload, headers: { Accept: 'application/json' } }
          : { method: 'POST', body: JSON.stringify(payload), headers: jsonHeaders };
      const res = await fetch('/cart/add.js', init);
      const data = await res.json();
      if (!res.ok) {
        const err = new Error(data.description || data.message || 'add failed');
        err.data = data;
        throw err;
      }
      const cart = await getCart();
      emit('cart:updated', cart);
      if (!opts.silent) emit('cart:added', { item: data, cart, trigger: opts.trigger || null });
      return data;
    },
    change: async (body) => {
      const res = await fetch('/cart/change.js', { method: 'POST', body: JSON.stringify(body), headers: jsonHeaders });
      const cart = await res.json();
      if (!res.ok) throw new Error(cart.description || 'change failed');
      emit('cart:updated', cart);
      return cart;
    },
    update: async (body) => {
      const res = await fetch('/cart/update.js', { method: 'POST', body: JSON.stringify(body), headers: jsonHeaders });
      const cart = await res.json();
      if (!res.ok) throw new Error(cart.description || 'update failed');
      emit('cart:updated', cart);
      return cart;
    },
  };

  /* Fetch a rendered section and return its root element */
  AB.fetchSection = async (sectionId, url) => {
    const base = new URL(url || window.location.href, window.location.origin);
    base.searchParams.set('section_id', sectionId);
    const res = await fetch(base.toString(), { headers: { Accept: 'text/html' } });
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc;
  };

  /* Globe pickup-point widget lives in [data-pickup-slot] elements. Section re-renders replace
     the markup, so move the already-injected widget into the fresh slot with the same id. */
  AB.keepPickupSlots = (oldRoot, newRoot) => {
    if (!oldRoot || !newRoot) return;
    oldRoot.querySelectorAll('[data-pickup-slot]').forEach((slot) => {
      const target = newRoot.querySelector('#' + slot.id);
      if (target && slot.childNodes.length) target.replaceChildren(...slot.childNodes);
    });
  };

  /* Generic one-click add: <form data-ab-add> or <button data-ab-add data-variant-id> */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-ab-add]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    if (btn) btn.disabled = true;
    try {
      await AB.cart.add(new FormData(form), { trigger: btn || form });
    } catch (err) {
      AB.toast((err && err.message) || (AB.t && AB.t.error) || 'Error');
    } finally {
      if (btn) btn.disabled = false;
    }
  });
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('button[data-ab-add][data-variant-id]');
    if (!btn) return;
    e.preventDefault();
    btn.disabled = true;
    try {
      await AB.cart.add({ id: parseInt(btn.dataset.variantId, 10), quantity: 1 }, { trigger: btn });
    } catch (err) {
      AB.toast((err && err.message) || (AB.t && AB.t.error) || 'Error');
    } finally {
      btn.disabled = false;
    }
  });

  /* Fallback when no drawer is on the page */
  document.addEventListener('cart:added', (e) => {
    if (window.AmmoCartDrawer) return;
    const t = AB.t || {};
    AB.toast(t.added || 'Added', { href: (AB.routes && AB.routes.cart) || '/cart', label: t.viewCart || 'Cart' });
  });
})();

/* ---- Scroll reveal ---------------------------------------------------------- */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const REVEAL_SELECTORS = [
    '.product-card',
    '.usp__item',
    '.series__card',
    '.collection__panel',
    '.cart-page__upsell-item',
    '.custom-cta__inner',
    '.footer__rating',
  ];

  // Elements already on screen when the script runs (first product row, hero USPs…) are never
  // hidden: hiding them would delay LCP and flash content that was visible before JS ran.
  const vh = () => window.innerHeight || document.documentElement.clientHeight;
  const inViewport = (el) => {
    const r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < vh();
  };
  const tag = (root) => {
    const seenParents = new Map();
    root.querySelectorAll(REVEAL_SELECTORS.join(',')).forEach((el) => {
      if (el.hasAttribute('data-reveal') || el.hasAttribute('data-reveal-skip')) return;
      if (inViewport(el)) {
        el.setAttribute('data-reveal-skip', '');
        return;
      }
      const parent = el.parentElement;
      const index = seenParents.get(parent) || 0;
      seenParents.set(parent, index + 1);
      el.setAttribute('data-reveal', '');
      el.style.setProperty('--reveal-index', Math.min(index, 8));
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  const arm = () => {
    tag(document);
    document.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => observer.observe(el));
  };
  window.AB.reveal = arm;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arm);
  } else {
    arm();
  }
  document.addEventListener('shopify:section:load', arm);
  document.addEventListener('ab:content:loaded', arm);
})();

/* ---- Dialog helpers --------------------------------------------------------- */
/* Close any modal dialog on backdrop click (cart drawer handles its own animated close). */
document.addEventListener('click', (e) => {
  const dialog = e.target.closest('dialog[open]');
  if (!dialog || dialog.id === 'cart-drawer') return;
  if (e.target === dialog) dialog.close();
});

/* Swipe-to-dismiss for bottom sheets: drag [data-sheet-handle] down to close. */
const armSheetHandles = (root) => {
  root.querySelectorAll('[data-sheet-handle]').forEach((handle) => {
    if (handle.dataset.armed) return;
    handle.dataset.armed = '1';
    const dialog = handle.closest('dialog');
    if (!dialog) return;
    let startY = 0;
    let delta = 0;
    let dragging = false;

    handle.addEventListener(
      'touchstart',
      (e) => {
        startY = e.touches[0].clientY;
        delta = 0;
        dragging = true;
        dialog.style.transition = 'none';
      },
      { passive: true }
    );
    handle.addEventListener(
      'touchmove',
      (e) => {
        if (!dragging) return;
        delta = Math.max(0, e.touches[0].clientY - startY);
        dialog.style.transform = 'translateY(' + delta + 'px)';
      },
      { passive: true }
    );
    const settle = () => {
      if (!dragging) return;
      dragging = false;
      const shouldClose = delta > 70;
      dialog.style.transition = 'transform 200ms ease';
      dialog.style.transform = shouldClose ? 'translateY(110%)' : 'translateY(0)';
      setTimeout(() => {
        if (shouldClose) dialog.close();
        dialog.style.transition = '';
        dialog.style.transform = '';
      }, 210);
    };
    handle.addEventListener('touchend', settle);
    handle.addEventListener('touchcancel', settle);
  });
};
armSheetHandles(document);
document.addEventListener('shopify:section:load', () => armSheetHandles(document));
document.addEventListener('ab:content:loaded', () => armSheetHandles(document));
